const RELATIVE_URL_BASE = 'https://pip-relative.invalid';
const ENCODED_PATH_SEPARATOR = /%(?:2f|5c)/i;
const ENCODED_DOT_SEGMENT = /(?:^|\/)(?:%2e){1,2}(?:\/|$)/i;

function containsControlCharacter(value: string): boolean {
    return Array.from(value).some((character) => {
        const codePoint = character.charCodeAt(0);
        return codePoint <= 31 || codePoint === 127;
    });
}

function effectivePort(url: URL): string {
    if (url.port) {
        return url.port;
    }
    return url.protocol === 'https:' ? '443' : '80';
}

function hasSafeUrlText(value: string): boolean {
    return value.length > 0 && value === value.trim() && !containsControlCharacter(value) && !value.includes('\\');
}

function hasUnsafeEncodedPath(value: string): boolean {
    const pathText = value.split(/[?#]/, 1)[0];
    return ENCODED_PATH_SEPARATOR.test(pathText) || ENCODED_DOT_SEGMENT.test(pathText);
}

function parseAllowedUrl(value: string): URL | undefined {
    if (!hasSafeUrlText(value) || hasUnsafeEncodedPath(value) || value.startsWith('//')) {
        return undefined;
    }

    try {
        const url = new URL(value);
        if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.username || url.password) {
            return undefined;
        }
        return url;
    } catch {
        return undefined;
    }
}

function pathMatches(candidatePath: string, allowedPath: string): boolean {
    const prefix = allowedPath.replace(/\/+$/, '') || '/';
    return prefix === '/' || candidatePath === prefix || candidatePath.startsWith(`${prefix}/`);
}

// Produces the smallest configured redirect value that can be safely revalidated server-side.
function getAllowedRedirectBase(allowedUrl: URL): string {
    const pathPrefix = allowedUrl.pathname.replace(/\/+$/, '');
    return pathPrefix ? `${allowedUrl.origin}${pathPrefix}` : allowedUrl.origin;
}

// Keeps allow-list matching rules shared between final redirect normalization and server validation targeting.
function getMatchingAllowedUrl(candidateUrl: URL, allowedUrls: readonly string[]): URL | undefined {
    for (const allowedValue of allowedUrls) {
        const allowedUrl = parseAllowedUrl(allowedValue);
        if (
            allowedUrl &&
            candidateUrl.protocol === allowedUrl.protocol &&
            candidateUrl.hostname === allowedUrl.hostname &&
            effectivePort(candidateUrl) === effectivePort(allowedUrl) &&
            pathMatches(candidateUrl.pathname, allowedUrl.pathname)
        ) {
            return allowedUrl;
        }
    }

    return undefined;
}

/**
 * Returns a normalized redirect destination when it is safe to use, otherwise undefined.
 * This module is shared by browser and server code and must remain runtime-neutral.
 */
export function getValidatedRedirectUrl(candidate: unknown, allowedUrls: readonly string[]): string | undefined {
    if (typeof candidate !== 'string' || !hasSafeUrlText(candidate) || hasUnsafeEncodedPath(candidate)) {
        return undefined;
    }

    // Application-relative routes are safe as long as they cannot be interpreted as
    // protocol-relative URLs or normalized into a different authority.
    if (candidate.startsWith('/')) {
        if (candidate.startsWith('//')) {
            return undefined;
        }
        try {
            const url = new URL(candidate, RELATIVE_URL_BASE);
            if (url.origin !== RELATIVE_URL_BASE) {
                return undefined;
            }
            return `${url.pathname}${url.search}${url.hash}`;
        } catch {
            return undefined;
        }
    }

    const candidateUrl = parseAllowedUrl(candidate);
    if (!candidateUrl) {
        return undefined;
    }

    const allowedUrl = getMatchingAllowedUrl(candidateUrl, allowedUrls);
    if (allowedUrl) {
        // Reconstruct the destination from the trusted configured origin. Only the
        // path, query, and fragment are retained from the candidate URL.
        return `${allowedUrl.origin}${candidateUrl.pathname}${candidateUrl.search}${candidateUrl.hash}`;
    }

    return undefined;
}

// Returns the reduced value sent to /api/validateRedirect so full paths and query params stay out of the POST body.
export function getRedirectValidationTarget(candidate: unknown, allowedUrls: readonly string[]): string | undefined {
    if (typeof candidate !== 'string' || !hasSafeUrlText(candidate) || hasUnsafeEncodedPath(candidate)) {
        return undefined;
    }

    if (candidate.startsWith('/')) {
        return getValidatedRedirectUrl(candidate, allowedUrls) ? '/' : undefined;
    }

    const candidateUrl = parseAllowedUrl(candidate);
    if (!candidateUrl) {
        return undefined;
    }

    const allowedUrl = getMatchingAllowedUrl(candidateUrl, allowedUrls);
    return allowedUrl ? getAllowedRedirectBase(allowedUrl) : undefined;
}

export function isRedirectUrlAllowed(candidate: unknown, allowedUrls: readonly string[]): boolean {
    return getValidatedRedirectUrl(candidate, allowedUrls) !== undefined;
}
